import { App, message } from 'antd';
import { useOpenGuardedFormModal } from '../useOpenGuardedFormModal';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import type { GetFileAccessesPayload } from 'interfaces/FileApiService';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileAccessNotificationSettingsForm,
  type FileAccessNotificationOverrideTarget,
} from '../../components/Forms/FileAccessNotificationSettingsForm';
import { FileAccessNotificationSettingOperationEnum } from '../../enums/FileAccessNotificationSettingOperationEnum';
import { FileAccessNotificationSettingTriggerEnum } from '../../enums/FileAccessNotificationSettingTriggerEnum';
import type File from '../../interfaces/File';
import type FileAccess from '../../interfaces/FileAccess';
import type { FileAccessNotificationSetting } from '../../interfaces/FileAccessNotificationSetting';
import type Group from '../../interfaces/Group';
import type GroupedFileAccess from '../../interfaces/GroupedFileAccess';
import type User from '../../interfaces/User';
import type { UserOrGroup } from '../../interfaces/UserOrGroup';
import {
  isGroupExemptAcrossRules,
  isUserExemptAcrossRules,
  minMaxAllowedByAge,
  clampAccessExpirationTicks,
  parseMandatoryExpirationAdditionalData,
  type MandatoryExpirationExemptionRule,
} from '../../utils/mandatoryExpirationExemptions/mandatoryExpirationExemptions';

/** Default for new shares: everything disabled. When user enables the main switch, owner is turned on and recipients stay off. */
const DEFAULT_NOTIFICATION_SETTINGS: FileAccessNotificationSetting = {
  enabled: false,
  recursive: true,
  notifyOwner: {
    enabled: false,
    operations: Object.values(FileAccessNotificationSettingOperationEnum),
  },
  notifyRecipients: {
    enabled: false,
    trigger: FileAccessNotificationSettingTriggerEnum.Anyone,
    operations: Object.values(FileAccessNotificationSettingOperationEnum),
  },
};

function areNotificationSettingsEqual(
  a: FileAccessNotificationSetting,
  b: FileAccessNotificationSetting,
): boolean {
  return (
    a.enabled === b.enabled &&
    a.recursive === b.recursive &&
    a.notifyOwner.enabled === b.notifyOwner.enabled &&
    a.notifyOwner.operations.length === b.notifyOwner.operations.length &&
    a.notifyOwner.operations.every((op, i) => b.notifyOwner.operations[i] === op) &&
    a.notifyRecipients.enabled === b.notifyRecipients.enabled &&
    a.notifyRecipients.trigger === b.notifyRecipients.trigger &&
    a.notifyRecipients.operations.length === b.notifyRecipients.operations.length &&
    a.notifyRecipients.operations.every((op, i) => b.notifyRecipients.operations[i] === op)
  );
}

/**
 * Normalizes notification settings from API response to form shape.
 * Ensures operations and trigger use enum values.
 */
function normalizeNotificationSettingsFromApi(
  raw: FileAccess['notificationSettings'],
): FileAccessNotificationSetting | null {
  if (!raw || typeof raw !== 'object') return null;
  const validOps = new Set(Object.values(FileAccessNotificationSettingOperationEnum));
  const ensureOps = (arr: unknown): FileAccessNotificationSettingOperationEnum[] => {
    if (!Array.isArray(arr)) return Object.values(FileAccessNotificationSettingOperationEnum);
    return arr.filter((op): op is FileAccessNotificationSettingOperationEnum =>
      validOps.has(op as FileAccessNotificationSettingOperationEnum),
    );
  };
  const rawTrigger = raw.notifyRecipients?.trigger;
  const trigger =
    rawTrigger === FileAccessNotificationSettingTriggerEnum.OwnerOnly ||
    String(rawTrigger).toLowerCase() === 'owneronly'
      ? FileAccessNotificationSettingTriggerEnum.OwnerOnly
      : FileAccessNotificationSettingTriggerEnum.Anyone;
  return {
    enabled: Boolean(raw.enabled),
    recursive: Boolean(raw.recursive),
    notifyOwner: {
      enabled: Boolean(raw.notifyOwner?.enabled),
      operations: ensureOps(raw.notifyOwner?.operations),
    },
    notifyRecipients: {
      enabled: Boolean(raw.notifyRecipients?.enabled),
      trigger,
      operations: ensureOps(raw.notifyRecipients?.operations),
    },
  };
}

/** Builds recipient key for an access (user-${id} or group-${id}). */
function getRecipientKeyFromAccess(access: FileAccess): string | null {
  if (access.user) return `user-${access.user.id}`;
  if (access.group) return `group-${access.group.id}`;
  return null;
}
import { FileService } from '../../services/fileApiService';
import { UserService } from '../../services/userApiService';
import { useAppStore } from '../../store/store';
import {
  computeCanElevateToEditor,
  isDirectShareExpirationPickerEnabled,
} from '../../utils/fileShareEditability';
import { copyToClipboard, generateUUID, msToTicks, ticksToMs } from '../../utils/utils';

const fileService = new FileService();
const userService = new UserService();

interface Props {
  selectedFiles: File[];
  onSubmit: (
    newUsers: User[],
    newGroups: Group[],
    fileAccessesToRemove: FileAccess[],
    fileAccessesToUpdate: FileAccess[],
    fileAccessesRevokeDenied: FileAccess[],
    sendMessage: boolean,
    message: string,
    readOnly: boolean,
    expiry: number,
    closeModalAfterSubmit: boolean,
    notificationSettings: FileAccessNotificationSetting | null,
    recipientNotificationOverrides: Record<string, FileAccessNotificationSetting | null>,
    currentAccessesForPayload: FileAccess[],
  ) => void;
}

interface FormState {
  users: User[];
  groups: Group[];
  fileAccessesToRemove: FileAccess[];
  fileAccessesToUpdate: FileAccess[];
  fileAccessesRevokeDenied: FileAccess[];
  sendMessage: boolean;
  message: string;
  readOnly: boolean;
  expiry: number;
  notificationSettings: FileAccessNotificationSetting | null;
  recipientNotificationOverrides: Record<string, FileAccessNotificationSetting | null>;
}

/**
 * Custom React hook for managing file sharing form state and logic.
 * Handles file access, user/group selection, permission changes, and related UI state.
 */
export type { FileAccessNotificationOverrideTarget };

function useFileShareForm({ selectedFiles, onSubmit }: Props) {
  const { t } = useTranslation();
  const { notification } = App.useApp();
  const openGuardedFormModal = useOpenGuardedFormModal();
  const [messageApi, contextHolder] = message.useMessage();
  const isModalLoading = useAppStore((s) => s.isModalLoading);
  const authenticatedUser = useAppStore((s) => s.authenticatedUser);
  const isNewFilesShareRecipientsReadOnly = useAppStore(
    (state) => state.appEvents.fileShare.isNewFilesShareRecipientsReadOnly,
  );
  const setIsNewFilesShareRecipientsReadOnly = useAppStore(
    (state) => state.appEvents.fileShare.setIsNewFilesShareRecipientsReadOnly,
  );
  const copyFileLinkToClipboard = useCallback((file: File) => {
    const link = `${window.location.origin}/file/${file.id}`;
    copyToClipboard(link);
  }, []);

  // State for file accesses and form
  const [fileAccesses, setFileAccesses] = useState<FileAccess[]>([]);
  const [originalFileAccesses, setOriginalFileAccesses] = useState<FileAccess[]>([]);
  const [form, setForm] = useState<FormState>({
    users: [],
    groups: [],
    fileAccessesToRemove: [],
    fileAccessesToUpdate: [],
    fileAccessesRevokeDenied: [],
    sendMessage: true,
    message: '',
    readOnly: true,
    expiry: 0,
    notificationSettings: { ...DEFAULT_NOTIFICATION_SETTINGS },
    recipientNotificationOverrides: {},
  });

  // UI state for date pickers and selection
  const [isLimitationsDatePickerOpen, setIsLimitationsDatePickerOpen] = useState(false);
  const [limitationsDatePickerValue, setLimitationsDatePickerValue] = useState<Dayjs | null>(null);

  const [isFileAccessDatePickerOpen, setIsFileAccessDatePickerOpen] = useState(false);
  const [fileAccessDatePickerValue, setFileAccessDatePickerValue] = useState<Dayjs | null>(null);

  const [selectedFileAccessesForDatePicker, setSelectedFileAccessesForDatePicker] = useState<
    FileAccess[]
  >([]);

  // Pagination and filtering state
  const [hasMore, setHasMore] = useState(true);
  const [, setTotalFileAccesses] = useState(0);
  const [filters, setFilters] = useState<GetFileAccessesPayload>({
    offset: 0,
    count: 100,
    search: '',
    files: selectedFiles.map((file) => file.id.toString()),
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditAllowed, setIsEditAllowed] = useState(false);

  // State for user details popover
  const [performedByUser, setPerformedByUser] = useState<User | null>(null);
  const [openPerformedByUserPopoverUuid, setOpenPerformedByUserPopoverUuid] = useState<
    string | null
  >(null);
  const [isLoadingPerformedByUser, setIsLoadingPerformedByUser] = useState(false);

  // Ref for scrollable container
  const scrollableDivRef = useRef<HTMLDivElement>(null);
  /** Bumps on each fetch so out-of-order `getFileAccesses` responses cannot overwrite paginated state. */
  const fileAccessesFetchGenerationRef = useRef(0);

  /**
   * Returns true if all selected files are owned by the authenticated user.
   */
  const areAllSelectedFilesMine = useMemo(() => {
    return selectedFiles.every((file) => file.ownerId === authenticatedUser?.id);
  }, [selectedFiles, authenticatedUser]);

  /**
   * True when every selected file is non-readonly; used with API `allowEdit` to relax
   * per-row edit rules for direct shares created by other users.
   */
  const allSelectedFilesWritable = useMemo(
    () => selectedFiles.length > 0 && selectedFiles.every((file) => !file.isReadOnly),
    [selectedFiles],
  );

  /**
   * Fetches file accesses for the selected files, updates state accordingly.
   */
  const getFileAccesses = useCallback(() => {
    const fetchGeneration = ++fileAccessesFetchGenerationRef.current;
    const payload: GetFileAccessesPayload = {
      ...filters,
      files: selectedFiles.map((file) => file.id.toString()),
    };

    setIsLoading(true);
    fileService
      .getFileAccesses(payload)
      .then((response) => {
        if (fetchGeneration !== fileAccessesFetchGenerationRef.current) {
          return;
        }
        const accesses = response?.accesses || [];
        setIsEditAllowed(response.allowEdit);
        setOriginalFileAccesses(response?.accesses || []);
        if (payload.offset === 0) {
          setFileAccesses(accesses);
          // Bind notification settings from API to form (each access → recipient override)
          const overrides: Record<string, FileAccessNotificationSetting> = {};
          let firstGlobal: FileAccessNotificationSetting | null = null;
          accesses.forEach((access: FileAccess) => {
            const key = getRecipientKeyFromAccess(access);
            const normalized = normalizeNotificationSettingsFromApi(access.notificationSettings);
            if (key && normalized) {
              overrides[key] = normalized;
              if (firstGlobal === null) firstGlobal = normalized;
            }
          });
          setForm((prev) => ({
            ...prev,
            notificationSettings: firstGlobal ?? { ...DEFAULT_NOTIFICATION_SETTINGS },
            recipientNotificationOverrides: overrides,
          }));
        } else {
          setFileAccesses((prev) => [...prev, ...accesses]);
          // Merge notification overrides for newly loaded accesses
          setForm((prev) => {
            const nextOverrides = { ...prev.recipientNotificationOverrides };
            accesses.forEach((access: FileAccess) => {
              const key = getRecipientKeyFromAccess(access);
              const normalized = normalizeNotificationSettingsFromApi(access.notificationSettings);
              if (key && normalized) nextOverrides[key] = normalized;
            });
            return { ...prev, recipientNotificationOverrides: nextOverrides };
          });
        }

        setTotalFileAccesses(response?.total || 0);
        setHasMore(payload.offset + (response?.accesses?.length ?? 0) < (response?.total ?? 0));
      })
      .finally(() => {
        if (fetchGeneration === fileAccessesFetchGenerationRef.current) {
          setIsLoading(false);
        }
      });
  }, [filters, selectedFiles]);

  /**
   * Returns the maximum allowed date for the file access expiration picker,
   * based on the selected accesses and their original expiration.
   * Note: This function uses getMaxDateForExistingFileAccess which is defined later.
   */
  const getMaxDateForFileAccessPicker = () => {
    if (!selectedFileAccessesForDatePicker.length) return undefined;

    // Get the general max date for existing accesses
    const generalMaxDate = getMaxDateForExistingFileAccess();

    if (areAllSelectedFilesMine) return generalMaxDate;

    const expirations = selectedFileAccessesForDatePicker
      .map((fa) => {
        const original = originalFileAccesses.find((o) => o.id === fa.id);
        if (
          original &&
          original.fileLimitation.accessExpiration &&
          original.fileLimitation.accessExpiration > 0
        ) {
          return original.fileLimitation.accessExpiration;
        }
        return null;
      })
      .filter((val): val is number => !!val && val > 0);

    const fromSelectedAccesses =
      expirations.length > 0 ? dayjs.utc(ticksToMs(Math.min(...expirations))).startOf('day') : null;

    if (generalMaxDate && fromSelectedAccesses) {
      // Use the most restrictive (earliest) date
      return generalMaxDate.valueOf() <= fromSelectedAccesses.valueOf()
        ? generalMaxDate
        : fromSelectedAccesses;
    }
    return generalMaxDate ?? fromSelectedAccesses ?? undefined;
  };

  /**
   * Handles infinite scroll for the file access list.
   * Loads more data when the user scrolls near the bottom.
   */
  const handleScroll = () => {
    if (!scrollableDivRef.current) return;
    if (isLoading || !hasMore) return;

    const { scrollTop, clientHeight, scrollHeight } = scrollableDivRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 50) {
      setFilters((prev) => ({
        ...prev,
        offset: prev.offset + prev.count,
      }));
      setIsLoading(true);
    }
  };

  /**
   * Handles form submission, passing current form state to the onSubmit callback.
   */
  const handleSubmit = (closeModalAfterSubmit: boolean = true) => {
    if (form.users.length > 0 || form.groups.length > 0) {
      setIsNewFilesShareRecipientsReadOnly(form.readOnly);
    }
    onSubmit(
      form.users,
      form.groups,
      form.fileAccessesToRemove,
      form.fileAccessesToUpdate,
      form.fileAccessesRevokeDenied,
      form.sendMessage,
      form.message,
      form.readOnly,
      form.expiry,
      closeModalAfterSubmit,
      form.notificationSettings,
      form.recipientNotificationOverrides,
      currentAccessesForPayload,
    );
  };

  /**
   * Updates a specific field in the form state.
   * @param field - The field name to update.
   * @param value - The new value for the field.
   */
  const handleFormFieldChange = (field: keyof FormState, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Set or clear notification override for a specific recipient.
   * @param recipientKey - e.g. `user-${id}` or `group-${id}` or email/name for new recipients.
   * @param value - Override settings or null to use global.
   */
  const setNotificationOverride = useCallback(
    (recipientKey: string, value: FileAccessNotificationSetting | null) => {
      setForm((prev) => ({
        ...prev,
        recipientNotificationOverrides: {
          ...prev.recipientNotificationOverrides,
          [recipientKey]: value,
        },
      }));
    },
    [],
  );

  /**
   * Opens the file access notification settings modal via Ant Design modal instance.
   * @param fileAccessNotificationOverrideTarget - When null, edits global settings; when set, edits override for that recipient.
   */
  const openFileAccessNotificationModal = useCallback(
    (fileAccessNotificationOverrideTarget: FileAccessNotificationOverrideTarget | null) => {
      const isOverride = fileAccessNotificationOverrideTarget !== null;
      const existingRecipientKeys = new Set(
        fileAccesses.map(getRecipientKeyFromAccess).filter(Boolean) as string[],
      );
      const currentRecipientKeys = [
        ...form.users.map((u) => `user-${u.id}`),
        ...form.groups.map((g) => `group-${g.id}`),
      ];
      const hasNewRecipients = currentRecipientKeys.some((k) => !existingRecipientKeys.has(k));
      const isSingleExistingRecipient =
        currentRecipientKeys.length === 1 && existingRecipientKeys.has(currentRecipientKeys[0]!);
      const useDefaultForGlobal = hasNewRecipients || isSingleExistingRecipient;
      const value: FileAccessNotificationSetting = isOverride
        ? (form.recipientNotificationOverrides[fileAccessNotificationOverrideTarget.recipientKey] ??
          form.notificationSettings ??
          DEFAULT_NOTIFICATION_SETTINGS)
        : useDefaultForGlobal
          ? { ...DEFAULT_NOTIFICATION_SETTINGS }
          : (form.notificationSettings ?? DEFAULT_NOTIFICATION_SETTINGS);
      const onChange = (v: FileAccessNotificationSetting) => {
        if (isOverride && fileAccessNotificationOverrideTarget) {
          setNotificationOverride(fileAccessNotificationOverrideTarget.recipientKey, v);
        } else {
          handleFormFieldChange('notificationSettings', v);
        }
      };
      const modalInstance = openGuardedFormModal({
        content: (
          <FileAccessNotificationSettingsForm
            value={value}
            onChange={onChange}
            isFolder={selectedFiles.some((f) => f.isFolder)}
            disabled={isModalLoading}
            fileAccessNotificationOverrideTarget={fileAccessNotificationOverrideTarget}
            onClose={() => modalInstance.destroy()}
          />
        ),
        modalProps: {
          width: 520,
        },
        title: isOverride
          ? `${t('fileAccessNotification.modalTitle')} – ${fileAccessNotificationOverrideTarget!.recipientLabel}`
          : t('fileAccessNotification.modalTitle'),
      });
    },
    [
      fileAccesses,
      form.notificationSettings,
      form.recipientNotificationOverrides,
      form.users,
      form.groups,
      handleFormFieldChange,
      openGuardedFormModal,
      isModalLoading,
      selectedFiles,
      setNotificationOverride,
      t,
    ],
  );

  /**
   * Reset the form by restoring fileAccesses to their original values and clearing any changes.
   */
  const handleResetForm = (emptyFileAccesses: boolean = false) => {
    // Restore fileAccesses to their original state
    if (emptyFileAccesses) {
      setOriginalFileAccesses([]);
      setTotalFileAccesses(0);
      setHasMore(false);
      setFileAccesses([]);
      setForm((prev) => ({
        ...prev,
        fileAccessesToRemove: [],
        fileAccessesToUpdate: [],
        fileAccessesRevokeDenied: [],
        recipientNotificationOverrides: {},
      }));
    } else {
      setFileAccesses(originalFileAccesses);
      // Restore notification overrides from original accesses
      const overrides: Record<string, FileAccessNotificationSetting> = {};
      let firstGlobal: FileAccessNotificationSetting | null = null;
      originalFileAccesses.forEach((access) => {
        const key = getRecipientKeyFromAccess(access);
        const normalized = normalizeNotificationSettingsFromApi(access.notificationSettings);
        if (key && normalized) {
          overrides[key] = normalized;
          if (firstGlobal === null) firstGlobal = normalized;
        }
      });
      setForm((prev) => ({
        ...prev,
        fileAccessesToRemove: [],
        fileAccessesToUpdate: [],
        fileAccessesRevokeDenied: [],
        notificationSettings: firstGlobal ?? prev.notificationSettings,
        recipientNotificationOverrides: overrides,
      }));
    }
  };

  /**
   * Returns true if the given file access is marked for removal in the form state.
   */
  const isFileAccessMarkedToRemove = useCallback(
    (fileAccess: FileAccess) => {
      return form?.fileAccessesToRemove?.find((f) => f.id === fileAccess.id);
    },
    [form.fileAccessesToRemove],
  );

  /**
   * Returns true if the given file access is marked for revoke-deny in the form state.
   */
  const isFileAccessMarkedToRevokeDeny = useCallback(
    (fileAccess: FileAccess) => {
      return form?.fileAccessesRevokeDenied?.find((f) => f.id === fileAccess.id);
    },
    [form.fileAccessesRevokeDenied],
  );

  /**
   * Returns true if the given file access belongs to the authenticated user.
   */
  const isFileAccessUserMe = useCallback(
    (fileAccess: FileAccess) => {
      return (
        fileAccess.user?.id === authenticatedUser?.id ||
        fileAccess.group?.userId === authenticatedUser?.id
      );
    },
    [authenticatedUser],
  );

  /**
   * Returns true if any new users or groups are selected in the form.
   */
  const areNewUsersOrGroupsSelected = useMemo(() => {
    return form.users.length > 0 || form.groups.length > 0;
  }, [form.users, form.groups]);

  /**
   * Returns true if there are changes to existing accesses (edit/remove/revoke-deny),
   * ignoring new user/group selections.
   */
  const haveExistingAccessesChanged = useMemo(() => {
    if (form.fileAccessesToRemove.length > 0) return true;
    if (form.fileAccessesRevokeDenied.length > 0) return true;

    if (form.fileAccessesToUpdate.length === 0) return false;

    // Compare updates against originals to ensure there's an actual diff
    const byId = new Map(originalFileAccesses.map((fa) => [fa.id, fa] as const));

    return form.fileAccessesToUpdate.some((updated) => {
      const original = byId.get(updated.id);
      if (!original) return true; // updated points to something not present anymore

      const o = original.fileLimitation;
      const u = updated.fileLimitation;

      const oExp = Number(o.accessExpiration || 0);
      const uExp = Number(u.accessExpiration || 0);

      return (
        o.canEdit !== u.canEdit ||
        o.canDownload !== u.canDownload ||
        oExp !== uExp ||
        Boolean(o.accessDenied) !== Boolean(u.accessDenied)
      );
    });
  }, [
    form.fileAccessesToRemove.length,
    form.fileAccessesRevokeDenied.length,
    form.fileAccessesToUpdate,
    originalFileAccesses,
  ]);

  /**
   * Current file accesses to send when updating only (no new users/groups): original minus removed, with updates applied.
   * Used to build update payload when only notification settings change (no permission edits).
   */
  const currentAccessesForPayload = useMemo(() => {
    return originalFileAccesses
      .filter((oa) => !form.fileAccessesToRemove.some((r) => r.id === oa.id))
      .map((oa) => {
        const updated = form.fileAccessesToUpdate.find((u) => u.id === oa.id);
        return updated ?? oa;
      });
  }, [originalFileAccesses, form.fileAccessesToRemove, form.fileAccessesToUpdate]);

  /**
   * Returns true if notification settings (global or per-recipient overrides) differ from initial/default.
   */
  const haveNotificationSettingsChanged = useMemo(() => {
    if (!form.notificationSettings) return false;
    if (!areNotificationSettingsEqual(form.notificationSettings, DEFAULT_NOTIFICATION_SETTINGS)) {
      return true;
    }
    return Object.keys(form.recipientNotificationOverrides).length > 0;
  }, [form.notificationSettings, form.recipientNotificationOverrides]);

  /**
   * Returns a unique list of file owners (sharedBy) for the selected files.
   */
  const fileOwners = useMemo(() => {
    const owners = selectedFiles.map((file) => file.sharedBy);
    return [...new Set(owners)];
  }, [selectedFiles]);

  /**
   * Returns a list of owner IDs for the selected files.
   */
  const fileOwnerIds = useMemo(() => {
    return selectedFiles.map((file) => file.ownerId);
  }, [selectedFiles]);

  /**
   * Returns a unique list of file owner emails, extracted from the sharedBy field.
   */
  const fileOwnersEmails = useMemo(() => {
    const emails = fileOwners
      .map((owner) => {
        const match = owner?.match(/\(([^)]+)\)/);
        return match ? match[1] : null;
      })
      .filter(Boolean);

    return [...new Set(emails)];
  }, [fileOwners]);

  /**
   * Loads and displays details for the user who performed an action, for popover display.
   * @param performedByUserId - The user ID to load.
   * @param uuid - The popover UUID to open.
   */
  const handleOpenPerformedByUserDetails = (performedByUserId: number, uuid: string) => {
    setOpenPerformedByUserPopoverUuid(uuid);

    if (performedByUserId === performedByUser?.id) {
      return;
    }

    setIsLoadingPerformedByUser(true);
    userService
      .getUser(performedByUserId)
      .then((user) => {
        setPerformedByUser(user);
      })
      .catch(() => {
        notification.error({
          title: t('errors.genericGetError', {
            resource: t('common.user'),
          }),
        });
      })
      .finally(() => {
        setIsLoadingPerformedByUser(false);
      });
  };

  /**
   * Groups file accesses by user or group, aggregating permissions and expiration.
   * @param fileAccesses - The list of file accesses to group.
   * @returns Array of grouped file accesses.
   */
  const groupFileAccesses = (fileAccesses: FileAccess[]): GroupedFileAccess[] => {
    const map = new Map<string, FileAccess[]>();

    const updatedFileAccesses = fileAccesses.map((fa) => {
      const override = form.fileAccessesToUpdate.find((fau) => fau.id === fa.id);
      return override ?? fa;
    });

    updatedFileAccesses.forEach((fa) => {
      let key: string | undefined = undefined;
      if (fa.user) key = `user-${fa.user.id}`;
      if (fa.group) key = `group-${fa.group.id}`;
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(fa);
    });

    return Array.from(map.entries()).map(([_, group]) => {
      let subject: UserOrGroup;
      if (group[0]?.user) {
        subject = { type: 'user', user: group[0]?.user };
      } else {
        subject = { type: 'group', group: group[0]?.group! };
      }
      const firstLimitation = group[0]?.fileLimitation;
      // Mixed may return: prefer non-inherited node limitation so UI shows Viewer/Editor (probe).
      // Revert: uncomment aggregation below; delete nodeLimitation/canEdit lines.
      // const canEdit = group.every((fa) => fa.fileLimitation.canEdit === firstLimitation?.canEdit)
      //   ? (firstLimitation?.canEdit ?? false)
      //   : 'mixed';
      const nodeLimitation = group.find((fa) => !fa.isInherited)?.fileLimitation ?? firstLimitation;
      const canEdit = nodeLimitation?.canEdit ?? false;
      const canDownload = group.every(
        (fa) => fa.fileLimitation.canDownload === firstLimitation?.canDownload,
      )
        ? (firstLimitation?.canDownload ?? false)
        : 'mixed';

      const accessExpirations = group
        .map((fa) => fa.fileLimitation.accessExpiration)
        .filter((x) => !!x && x > 0);

      let accessExpiration: number | 'mixed' = 0;
      if (accessExpirations.length === 0) {
        accessExpiration = 0;
      } else if (accessExpirations.every((val) => val === accessExpirations[0])) {
        accessExpiration = accessExpirations[0] ?? 0;
      } else {
        accessExpiration = 'mixed';
      }

      return {
        uuid: generateUUID(),
        subject,
        canEdit,
        canDownload,
        accessExpiration,
        fileAccessIds: group.map((fa) => fa.id),
        originalFileAccesses: group,
      };
    });
  };

  /**
   * Memoized grouped file accesses for the current fileAccesses state.
   */
  const groupedFileAccesses = useMemo<GroupedFileAccess[]>(() => {
    return groupFileAccesses(fileAccesses);
  }, [fileAccesses, form.fileAccessesToUpdate]);

  /**
   * Memoized grouped file accesses for the original file accesses.
   */
  const originalGroupedFileAccesses = useMemo<GroupedFileAccess[]>(() => {
    return groupFileAccesses(originalFileAccesses);
  }, [originalFileAccesses]);

  /**
   * Parsed DenyFileUnlimitedExpiration rules for selected files (exemptions + max days).
   */
  const mandatoryExpirationRules = useMemo<MandatoryExpirationExemptionRule[]>(() => {
    if (!selectedFiles?.length) return [];
    return selectedFiles
      .map(
        (f) =>
          f.fileLimitations?.find((l) => l.name === 'DenyFileUnlimitedExpiration')?.additionalData,
      )
      .map((ad) => parseMandatoryExpirationAdditionalData(ad))
      .filter((rule): rule is MandatoryExpirationExemptionRule => rule != null);
  }, [selectedFiles]);

  /**
   * Returns the minimum allowed expiration days for the selected files,
   * based on file limitations (DenyFileUnlimitedExpiration).
   */
  const filesMaxExpirationDays = useMemo(
    () => minMaxAllowedByAge(mandatoryExpirationRules),
    [mandatoryExpirationRules],
  );

  const isSubjectExemptFromMandatoryExpiration = useCallback(
    (subject: UserOrGroup) => {
      if (!mandatoryExpirationRules.length) return false;
      if (subject.type === 'user') {
        return isUserExemptAcrossRules(subject.user.email, mandatoryExpirationRules);
      }
      return isGroupExemptAcrossRules(subject.group.id, mandatoryExpirationRules);
    },
    [mandatoryExpirationRules],
  );

  const isAccessExemptFromMandatoryExpiration = useCallback(
    (fileAccess: FileAccess) => {
      if (!mandatoryExpirationRules.length) return false;
      if (fileAccess.user) {
        return isUserExemptAcrossRules(fileAccess.user.email, mandatoryExpirationRules);
      }
      if (fileAccess.group) {
        return isGroupExemptAcrossRules(fileAccess.group.id, mandatoryExpirationRules);
      }
      return false;
    },
    [mandatoryExpirationRules],
  );

  /**
   * New-share global expiry is forced only when at least one selected recipient is subject.
   * No recipients yet → keep prior force behavior when the file limitation exists.
   */
  const newShareRequiresMandatoryExpiration = useMemo(() => {
    if (typeof filesMaxExpirationDays !== 'number') return false;
    if (!areNewUsersOrGroupsSelected) return true;
    const anyUserSubject = form.users.some(
      (user) => !isUserExemptAcrossRules(user.email, mandatoryExpirationRules),
    );
    const anyGroupSubject = form.groups.some(
      (group) => !isGroupExemptAcrossRules(group.id, mandatoryExpirationRules),
    );
    return anyUserSubject || anyGroupSubject;
  }, [
    filesMaxExpirationDays,
    areNewUsersOrGroupsSelected,
    form.users,
    form.groups,
    mandatoryExpirationRules,
  ]);

  /**
   * IDs of existing accesses without expiration that are actually editable from the UI
   * using the same visibility/disabled logic as the per-row date picker button.
   */
  const editableMissingExpirationAccessIds = useMemo(() => {
    if (groupedFileAccesses.length === 0) return new Set<number>();

    const missingIds = new Set(
      currentAccessesForPayload
        .filter(
          (fa) => !fa.fileLimitation.accessExpiration || fa.fileLimitation.accessExpiration === 0,
        )
        .map((fa) => fa.id),
    );
    if (missingIds.size === 0) return new Set<number>();

    const editableIds = new Set<number>();
    for (const grouped of groupedFileAccesses) {
      const groupMissingIds = grouped.fileAccessIds.filter((id) => missingIds.has(id));
      if (groupMissingIds.length === 0) continue;

      if (isSubjectExemptFromMandatoryExpiration(grouped.subject)) continue;

      const mainFileAccess = grouped.originalFileAccesses[0];
      if (!mainFileAccess) continue;

      const pickerVisible =
        !mainFileAccess.isInherited && isEditAllowed && !isFileAccessUserMe(mainFileAccess);
      if (!pickerVisible) continue;

      const originalGroup = originalGroupedFileAccesses.find(
        (g) =>
          g.subject.type === grouped.subject.type &&
          ((g.subject.type === 'user' &&
            grouped.subject.type === 'user' &&
            g.subject.user.id === grouped.subject.user?.id) ||
            (g.subject.type === 'group' &&
              grouped.subject.type === 'group' &&
              g.subject.group.id === grouped.subject.group?.id)),
      );

      const allOriginalAreEditor =
        originalGroup?.originalFileAccesses.every((fa) => fa.fileLimitation.canEdit === true) ??
        false;
      const allPerformedByMe = grouped.originalFileAccesses.every(
        (fa) => fa.performedByUserId === authenticatedUser?.id,
      );

      const canElevateToEditor = computeCanElevateToEditor(
        areAllSelectedFilesMine,
        allOriginalAreEditor,
        allPerformedByMe,
      );

      const expirationPickerEnabled = isDirectShareExpirationPickerEnabled(
        isEditAllowed,
        allSelectedFilesWritable,
        canElevateToEditor,
        grouped.canEdit,
      );
      if (!expirationPickerEnabled) continue;

      groupMissingIds.forEach((id) => editableIds.add(id));
    }

    return editableIds;
  }, [
    groupedFileAccesses,
    currentAccessesForPayload,
    originalGroupedFileAccesses,
    isEditAllowed,
    allSelectedFilesWritable,
    authenticatedUser,
    areAllSelectedFilesMine,
    isFileAccessUserMe,
    isSubjectExemptFromMandatoryExpiration,
  ]);

  /**
   * Show bulk button when at least one missing-expiration access is editable.
   */
  const perAccessExpirationPickersAllowBulkMaxExpiration = useMemo(() => {
    return editableMissingExpirationAccessIds.size > 0;
  }, [editableMissingExpirationAccessIds]);

  /**
   * Returns sharing limits for new shares, including max expiry date and edit permission.
   * Used to determine if "Editor" option should be disabled and the max expiry date.
   */
  const newSharingLimits = useMemo(() => {
    if (areAllSelectedFilesMine)
      return {
        maxExpiryDate: undefined,
        canEditAllowed: true,
      };

    const expirations = originalFileAccesses
      .map((fa) => fa.fileLimitation.accessExpiration)
      .filter((x) => !!x && x > 0);

    const maxExpiryDate =
      expirations.length > 0
        ? dayjs.utc(ticksToMs(Math.min(...expirations))).startOf('day')
        : undefined;

    const canEditFromExistingAccesses = originalFileAccesses.some(
      (fa) => fa.fileLimitation.canEdit === true,
    );
    const canEditFromSelectedFiles =
      !areAllSelectedFilesMine && selectedFiles.every((file) => !file.isReadOnly);
    const canEditAllowed = canEditFromExistingAccesses || canEditFromSelectedFiles;

    return {
      maxExpiryDate,
      canEditAllowed,
    };
  }, [areAllSelectedFilesMine, originalFileAccesses, selectedFiles]);

  /** Restore last new-share permission once accesses have loaded (when both options are available). */
  const hasRestoredShareReadOnlyRef = useRef(false);
  useEffect(() => {
    if (isLoading || hasRestoredShareReadOnlyRef.current) return;
    hasRestoredShareReadOnlyRef.current = true;
    setForm((previous) => ({
      ...previous,
      readOnly: newSharingLimits.canEditAllowed ? isNewFilesShareRecipientsReadOnly : true,
    }));
  }, [isLoading, newSharingLimits.canEditAllowed, isNewFilesShareRecipientsReadOnly]);

  useEffect(() => {
    if (newSharingLimits.canEditAllowed || form.readOnly) return;
    setForm((previous) => ({ ...previous, readOnly: true }));
  }, [newSharingLimits.canEditAllowed, form.readOnly]);

  /**
   * True when at least one selected file has DenyFileUnlimitedExpiration and at least one
   * existing access (in currentAccessesForPayload) has no expiration set. When true, show
   * error and disable Save until expiration is set for those accesses.
   */
  const existingAccessesRequireExpirationError = useMemo(() => {
    if (filesMaxExpirationDays === null) return false;
    return editableMissingExpirationAccessIds.size > 0;
  }, [filesMaxExpirationDays, editableMissingExpirationAccessIds]);

  /**
   * Returns true if the submit button should be disabled, based on loading state and form changes.
   * Also disabled when existing accesses require an expiration date but one or more have none set.
   */
  const isSubmitButtonDisabled = useMemo(() => {
    if (isLoading) return true;
    if (!areNewUsersOrGroupsSelected && existingAccessesRequireExpirationError) return true;
    if (areNewUsersOrGroupsSelected) {
      return form.users.length === 0 && form.groups.length === 0;
    }
    return !haveExistingAccessesChanged && !haveNotificationSettingsChanged;
  }, [
    isLoading,
    existingAccessesRequireExpirationError,
    areNewUsersOrGroupsSelected,
    form.users.length,
    form.groups.length,
    haveExistingAccessesChanged,
    haveNotificationSettingsChanged,
  ]);

  /**
   * Translated reason when Share/Save is disabled (null while loading or when enabled).
   * Missing-expiry case stays on the Alert only — tooltip would be redundant.
   */
  const submitDisabledReason = useMemo(() => {
    if (isLoading) return null;
    if (areNewUsersOrGroupsSelected) {
      if (form.users.length === 0 && form.groups.length === 0) {
        return t('common.shareDisabledNoRecipients');
      }
      return null;
    }
    if (!haveExistingAccessesChanged && !haveNotificationSettingsChanged) {
      return t('common.shareDisabledNoChanges');
    }
    return null;
  }, [
    isLoading,
    areNewUsersOrGroupsSelected,
    form.users.length,
    form.groups.length,
    haveExistingAccessesChanged,
    haveNotificationSettingsChanged,
    t,
  ]);

  /**
   * Returns true if there's a mandatory expiration restriction for existing file accesses.
   * File-level DenyFileUnlimitedExpiration still applies when exemptions leave at least one
   * subject recipient; other existing access expirations also force the restriction.
   */
  const hasMandatoryExpirationRestriction = useCallback(() => {
    if (areAllSelectedFilesMine) return false;

    if (typeof filesMaxExpirationDays === 'number') return true;

    // Check if there are other accesses with expiration
    const otherAccessesExpirations = originalFileAccesses
      .map((fa) => fa.fileLimitation.accessExpiration)
      .filter((x) => !!x && x > 0);

    return otherAccessesExpirations.length > 0;
  }, [areAllSelectedFilesMine, filesMaxExpirationDays, originalFileAccesses]);

  /**
   * True when this subject is bound by DenyFileUnlimitedExpiration (not exempt).
   */
  const isSubjectBoundByMandatoryExpiration = useCallback(
    (subject: UserOrGroup) => {
      if (typeof filesMaxExpirationDays !== 'number') return false;
      return !isSubjectExemptFromMandatoryExpiration(subject);
    },
    [filesMaxExpirationDays, isSubjectExemptFromMandatoryExpiration],
  );
  /**
   * Returns the maximum allowed date for existing file access expiration picker,
   * based on both file-level limitations and other existing accesses.
   */
  const getMaxDateForExistingFileAccess = useCallback(() => {
    if (areAllSelectedFilesMine) return undefined;

    // Restriction from file-level limitations
    const fromFiles =
      typeof filesMaxExpirationDays === 'number'
        ? dayjs
            .utc()
            .add(filesMaxExpirationDays - 1, 'day')
            .startOf('day')
        : null;

    // Restriction from other existing accesses (excluding the ones being edited)
    const otherAccessesExpirations = originalFileAccesses
      .map((fa) => fa.fileLimitation.accessExpiration)
      .filter((x) => !!x && x > 0);

    const fromAccesses =
      otherAccessesExpirations.length > 0
        ? dayjs.utc(ticksToMs(Math.min(...otherAccessesExpirations))).startOf('day')
        : null;

    if (fromAccesses && fromFiles) {
      // Use the most restrictive (earliest) date
      return fromAccesses.valueOf() <= fromFiles.valueOf() ? fromAccesses : fromFiles;
    }
    return fromAccesses ?? fromFiles ?? undefined;
  }, [areAllSelectedFilesMine, filesMaxExpirationDays, originalFileAccesses]);

  /**
   * Handles changes to file access limitations (permissions, expiration, etc).
   * Updates the form state with the new limitation for the given file access.
   * If canEdit is changed and there's a mandatory expiration restriction, it forces the expiration date.
   * @param fileAccess - The file access to update.
   * @param field - The limitation field to change.
   * @param value - The new value for the limitation field.
   */
  const handleFileLimitationChange = useCallback(
    (fileAccess: FileAccess, field: keyof FileAccess['fileLimitation'], value: any) => {
      setForm((prev) => {
        const original = fileAccess.fileLimitation;
        const existingOverride = prev.fileAccessesToUpdate.find((fa) => fa.id === fileAccess.id);
        const base = existingOverride ? existingOverride.fileLimitation : original;
        const newLimitation: FileAccess['fileLimitation'] = {
          ...base,
          [field]: value,
        };

        // If canEdit is being changed and there's a mandatory expiration restriction,
        // force the expiration date if it's not already set (skip exempt recipients)
        if (field === 'canEdit' && !isAccessExemptFromMandatoryExpiration(fileAccess)) {
          // Check if there's a file-level limitation (DenyFileUnlimitedExpiration)
          const hasFileLevelRestriction = typeof filesMaxExpirationDays === 'number';

          // Get max date from existing access restrictions
          const maxDateFromAccesses = getMaxDateForExistingFileAccess();

          // Calculate max date from file-level limitation if present
          const maxDateFromFiles = hasFileLevelRestriction
            ? dayjs
                .utc()
                .add(filesMaxExpirationDays - 1, 'day')
                .startOf('day')
            : null;

          // Use the most restrictive date (earliest) if both exist, otherwise use whichever is available
          const maxDate =
            maxDateFromAccesses && maxDateFromFiles
              ? maxDateFromAccesses.valueOf() <= maxDateFromFiles.valueOf()
                ? maxDateFromAccesses
                : maxDateFromFiles
              : (maxDateFromAccesses ?? maxDateFromFiles ?? null);

          // If there's a restriction and no expiration is set, force it
          if (
            maxDate &&
            (!newLimitation.accessExpiration || newLimitation.accessExpiration === 0)
          ) {
            // Force expiration to the maximum allowed date (end of day)
            newLimitation.accessExpiration = msToTicks(maxDate.endOf('day').valueOf());
          }
        }

        // Prevent removing expiration if there's a mandatory restriction
        // This must happen BEFORE any other logic to ensure it can't be bypassed
        if (
          field === 'accessExpiration' &&
          (value === 0 || value === null || value === undefined)
        ) {
          const accessExempt = isAccessExemptFromMandatoryExpiration(fileAccess);
          // Check restriction directly from filesMaxExpirationDays to be absolutely sure
          const hasFileLevelRestriction =
            !accessExempt && typeof filesMaxExpirationDays === 'number';
          const hasOtherRestriction =
            !areAllSelectedFilesMine &&
            originalFileAccesses.some(
              (fa) => fa.fileLimitation.accessExpiration && fa.fileLimitation.accessExpiration > 0,
            );

          if (hasFileLevelRestriction || (!accessExempt && hasOtherRestriction)) {
            // Don't allow removing expiration, restore the current value
            // Use the base (which might be an override) or fall back to original
            const currentExpiration = base.accessExpiration || original.accessExpiration;
            if (currentExpiration && currentExpiration > 0) {
              // Keep the existing expiration - DO NOT ALLOW REMOVAL
              newLimitation.accessExpiration = currentExpiration;
            } else {
              // If no expiration exists, set to max allowed
              const maxDate = getMaxDateForExistingFileAccess();
              if (maxDate) {
                newLimitation.accessExpiration = msToTicks(maxDate.endOf('day').valueOf());
              } else {
                // Fallback: keep original if maxDate is somehow undefined
                newLimitation.accessExpiration = original.accessExpiration;
              }
            }
          }
        }

        let newUpdates = prev.fileAccessesToUpdate.filter((fa) => fa.id !== fileAccess.id);
        if (JSON.stringify(newLimitation) !== JSON.stringify(original)) {
          const entry: FileAccess = {
            ...fileAccess,
            fileLimitation: newLimitation,
          };
          newUpdates = [...newUpdates, entry];
        }
        return {
          ...prev,
          fileAccessesToUpdate: newUpdates,
        };
      });
    },
    [
      getMaxDateForExistingFileAccess,
      areAllSelectedFilesMine,
      filesMaxExpirationDays,
      msToTicks,
      isAccessExemptFromMandatoryExpiration,
      originalFileAccesses,
    ],
  );

  /**
   * Returns the maximum allowed date for the new sharing date picker,
   * based on both access and file-level restrictions.
   * For received files (not owned by user), existing access expirations take priority over file-level limitations.
   */
  const newSharingDatePickerMaxDate = useMemo(() => {
    // Restriction from existing accesses (already calculated with .startOf('day') in newSharingLimits)
    const fromAccesses = newSharingLimits.maxExpiryDate || null;

    // Restriction from file-level limitations — only when a subject recipient remains
    const fromFiles =
      newShareRequiresMandatoryExpiration && typeof filesMaxExpirationDays === 'number'
        ? dayjs
            .utc()
            .add(filesMaxExpirationDays - 1, 'day')
            .startOf('day')
        : null;

    // If files are owned by the user, consider both restrictions and use the most restrictive
    if (areAllSelectedFilesMine) {
      if (fromAccesses && fromFiles) {
        // Use the most restrictive (earliest) date
        return fromAccesses.valueOf() <= fromFiles.valueOf() ? fromAccesses : fromFiles;
      }
      return fromAccesses ?? fromFiles ?? undefined;
    }

    // Received files: still honor mandatory maxDays (most restrictive with inherited access expiry).
    // Ignoring filesMaxExpirationDays here let folder-inherited far dates exceed policy on re-share.
    if (fromAccesses && fromFiles) {
      return fromAccesses.valueOf() <= fromFiles.valueOf() ? fromAccesses : fromFiles;
    }
    return fromAccesses ?? fromFiles ?? undefined;
  }, [
    newSharingLimits.maxExpiryDate,
    filesMaxExpirationDays,
    areAllSelectedFilesMine,
    newShareRequiresMandatoryExpiration,
  ]);

  /**
   * True when a maximum expiration date can be applied (picker + policy).
   */
  const canApplyMaxExpirationToMissingAccesses = useMemo(() => {
    return (
      editableMissingExpirationAccessIds.size > 0 &&
      !!(getMaxDateForExistingFileAccess() ?? newSharingDatePickerMaxDate)
    );
  }, [
    editableMissingExpirationAccessIds,
    getMaxDateForExistingFileAccess,
    newSharingDatePickerMaxDate,
  ]);

  /**
   * Share again after inherited deny: queue revokeDenied and clamp expiry under mandatory max
   * so the follow-up access update (if any) cannot send MANDATORY_EXPIRATION_EXCEEDED.
   */
  const handleShareAgain = useCallback(
    (accesses: FileAccess[]) => {
      const denied = accesses.filter((fa) => fa.isInherited && fa.fileLimitation.accessDenied);
      if (denied.length === 0) return;

      setForm((prev) => {
        const revokeIds = new Set(prev.fileAccessesRevokeDenied.map((f) => f.id));
        const nextRevoke = [
          ...prev.fileAccessesRevokeDenied,
          ...denied.filter((fa) => !revokeIds.has(fa.id)),
        ];

        let nextUpdates = [...prev.fileAccessesToUpdate];
        if (typeof filesMaxExpirationDays === 'number') {
          const nowMs = Date.now();
          for (const fa of denied) {
            if (isAccessExemptFromMandatoryExpiration(fa)) continue;
            const base = nextUpdates.find((u) => u.id === fa.id) ?? fa;
            const clamped = clampAccessExpirationTicks(
              base.fileLimitation.accessExpiration || 0,
              filesMaxExpirationDays,
              nowMs,
              msToTicks,
            );
            if (clamped === (base.fileLimitation.accessExpiration || 0)) continue;
            const patched: FileAccess = {
              ...base,
              fileLimitation: {
                ...base.fileLimitation,
                accessExpiration: clamped,
              },
            };
            nextUpdates = nextUpdates.filter((u) => u.id !== fa.id);
            nextUpdates.push(patched);
          }
        }

        return {
          ...prev,
          fileAccessesRevokeDenied: nextRevoke,
          fileAccessesToUpdate: nextUpdates,
        };
      });
    },
    [filesMaxExpirationDays, isAccessExemptFromMandatoryExpiration, msToTicks],
  );

  const handleCancelShareAgain = useCallback((accessIds: number[]) => {
    const idSet = new Set(accessIds);
    setForm((prev) => ({
      ...prev,
      fileAccessesRevokeDenied: prev.fileAccessesRevokeDenied.filter((f) => !idSet.has(f.id)),
      // Drop expiry patches that were only queued for share-again on these accesses.
      fileAccessesToUpdate: prev.fileAccessesToUpdate.filter((u) => !idSet.has(u.id)),
    }));
  }, []);

  /**
   * Sets all existing accesses that still have no expiration to the maximum date allowed
   * (same policy as the per-access date picker: company file limits and other accesses).
   */
  const handleApplyMaxExpirationToAllMissing = useCallback(() => {
    const maxDate = getMaxDateForExistingFileAccess() ?? newSharingDatePickerMaxDate;
    if (!maxDate) return;
    const ticks = msToTicks(dayjs.utc(maxDate).endOf('day').valueOf());

    setForm((prev) => {
      const targets = originalFileAccesses
        .filter((oa) => !prev.fileAccessesToRemove.some((r) => r.id === oa.id))
        .filter((oa) => editableMissingExpirationAccessIds.has(oa.id))
        .filter((oa) => {
          const updated = prev.fileAccessesToUpdate.find((u) => u.id === oa.id);
          const current = updated ?? oa;
          return (
            !current.fileLimitation.accessExpiration ||
            current.fileLimitation.accessExpiration === 0
          );
        });

      if (targets.length === 0) return prev;

      const targetIds = new Set(targets.map((t) => t.id));
      let newUpdates = prev.fileAccessesToUpdate.filter((u) => !targetIds.has(u.id));

      for (const fileAccess of targets) {
        const existingOverride = prev.fileAccessesToUpdate.find((fa) => fa.id === fileAccess.id);
        const originalLimitation = fileAccess.fileLimitation;
        const base = existingOverride ? existingOverride.fileLimitation : originalLimitation;
        const newLimitation: FileAccess['fileLimitation'] = {
          ...base,
          accessExpiration: ticks,
        };

        if (JSON.stringify(newLimitation) !== JSON.stringify(originalLimitation)) {
          newUpdates.push({
            ...(existingOverride ?? fileAccess),
            fileLimitation: newLimitation,
          });
        }
      }

      return {
        ...prev,
        fileAccessesToUpdate: newUpdates,
      };
    });
  }, [
    editableMissingExpirationAccessIds,
    getMaxDateForExistingFileAccess,
    newSharingDatePickerMaxDate,
    originalFileAccesses,
    msToTicks,
  ]);

  // Fetch file accesses when filters or selected files change
  useEffect(() => {
    getFileAccesses();
  }, [getFileAccesses]);

  // Set default expiration date in the form if not already set and a max date is available
  // Also update if form.expiry exceeds the new max date (for received files)
  useEffect(() => {
    if (!newSharingDatePickerMaxDate) return;

    // Convert UTC date to local date for DatePicker (extract year, month, day from UTC)
    // Use valueOf() to get timestamp and recreate UTC date to avoid timezone issues
    const dtUtc = dayjs.utc(newSharingDatePickerMaxDate.valueOf());
    const year = dtUtc.year();
    const month = dtUtc.month() + 1; // Dayjs months are 0-indexed
    const day = dtUtc.date();
    const localDate = dayjs(`${year}-${month}-${day}`, 'YYYY-M-D');

    // Calculate the UTC end of day for the actual expiry value
    // Create a new UTC date with the extracted year, month, day to avoid timezone issues
    const maxDateUtc = dayjs.utc(`${year}-${month}-${day}`, 'YYYY-M-D').endOf('day');
    const maxDateUtcTicks = msToTicks(maxDateUtc.valueOf());

    // If form.expiry is not set, set it to the max date
    if (!form.expiry) {
      setLimitationsDatePickerValue(localDate);
      handleFormFieldChange('expiry', maxDateUtcTicks);
    } else if (!areAllSelectedFilesMine && form.expiry > maxDateUtcTicks) {
      // For received files, if current expiry exceeds the new max, update it to the max
      setLimitationsDatePickerValue(localDate);
      handleFormFieldChange('expiry', maxDateUtcTicks);
    }
  }, [form.expiry, newSharingDatePickerMaxDate, areAllSelectedFilesMine]);

  return {
    fileAccesses,
    originalFileAccesses,
    form,
    isLimitationsDatePickerOpen,
    limitationsDatePickerValue,
    isFileAccessDatePickerOpen,
    fileAccessDatePickerValue,
    selectedFileAccessesForDatePicker,
    hasMore,
    setTotalFileAccesses,
    filters,
    setFilters,
    isLoading,
    isEditAllowed,
    performedByUser,
    openPerformedByUserPopoverUuid,
    isLoadingPerformedByUser,
    scrollableDivRef,
    handleScroll,
    handleSubmit,
    handleFormFieldChange,
    handleFileLimitationChange,
    handleResetForm,
    isFileAccessMarkedToRemove,
    isFileAccessMarkedToRevokeDeny,
    isFileAccessUserMe,
    areNewUsersOrGroupsSelected,
    haveExistingAccessesChanged,
    haveNotificationSettingsChanged,
    isSubmitButtonDisabled,
    submitDisabledReason,
    existingAccessesRequireExpirationError,
    canApplyMaxExpirationToMissingAccesses,
    handleApplyMaxExpirationToAllMissing,
    perAccessExpirationPickersAllowBulkMaxExpiration,
    fileOwners,
    fileOwnerIds,
    fileOwnersEmails,
    handleOpenPerformedByUserDetails,
    groupedFileAccesses,
    originalGroupedFileAccesses,
    newSharingLimits,
    filesMaxExpirationDays,
    newSharingDatePickerMaxDate,
    isModalLoading,
    copyFileLinkToClipboard,
    contextHolder,
    setIsLimitationsDatePickerOpen,
    setLimitationsDatePickerValue,
    setIsFileAccessDatePickerOpen,
    setFileAccessDatePickerValue,
    setSelectedFileAccessesForDatePicker,
    setHasMore,
    setIsLoading,
    setIsEditAllowed,
    areAllSelectedFilesMine,
    setOpenPerformedByUserPopoverUuid,
    setPerformedByUser,
    getMaxDateForFileAccessPicker,
    getMaxDateForExistingFileAccess,
    hasMandatoryExpirationRestriction,
    isSubjectBoundByMandatoryExpiration,
    handleShareAgain,
    handleCancelShareAgain,
    messageApi,
    authenticatedUser,
    setNotificationOverride,
    openFileAccessNotificationModal,
  };
}

export default useFileShareForm;
